/**
 * CSS unit handling for scroll trigger positions.
 *
 * GSAP's `_offsetToPx` understands exactly three things: its own keywords, `%`,
 * and `parseFloat` (i.e. pixels). Anything else degrades silently — `top-=50vh`
 * becomes `top-=50px`, with no error and no NaN. So the Designer accepts
 * `vh`/`vw`/`rem` for authoring and the runtime rewrites them to pixels before
 * GSAP ever sees the string.
 *
 * Deliberately Zod-free. The published runtime does not import this module (it
 * never takes a value import from the schema package, to keep Zod out of the
 * 29KB bundle budget); it carries a copy instead. The next PR in the stack
 * (#122802) adds that copy in `packages/systems/ix3/runtime/src/utils.ts`,
 * together with `runtime/src/__tests__/utils.scrollUnits.test.ts`, which mirrors
 * this module's tests so drift fails one of the suites. Keep the two in sync.
 */

/** Units accepted anywhere a number appears in a scroll position. */
export const SCROLL_POSITION_UNITS = ['%', 'px', 'vh', 'vw', 'rem'] as const;
export type ScrollPositionUnit = (typeof SCROLL_POSITION_UNITS)[number];

/**
 * The subset the runtime rewrites to pixels. `%` is left alone because GSAP
 * resolves it against the element or the scroller — a referent we can't reach
 * lexically — and `px` needs no rewrite, since `parseFloat('50px')` is already
 * 50.
 */
export const RESOLVED_SCROLL_UNITS = ['vh', 'vw', 'rem'] as const;
export type ResolvedScrollUnit = (typeof RESOLVED_SCROLL_UNITS)[number];

/** Pixels per one unit, measured fresh on each ScrollTrigger refresh. */
export type ScrollUnitFactors = Record<ResolvedScrollUnit, number>;

/** Fallback when the root font size can't be read or parses to 0. */
export const DEFAULT_ROOT_FONT_SIZE_PX = 16;

const NUMBER_SOURCE = '\\d+(?:\\.\\d+)?';
const RESOLVED_UNIT_SOURCE = RESOLVED_SCROLL_UNITS.join('|');

/** Non-global on purpose: `.test()` on a global RegExp is stateful. */
const HAS_RESOLVED_UNIT = new RegExp(
  `${NUMBER_SOURCE}(?:${RESOLVED_UNIT_SOURCE})`
);
const RESOLVED_UNIT = new RegExp(
  `(${NUMBER_SOURCE})(${RESOLVED_UNIT_SOURCE})`,
  'g'
);

/**
 * Whether `resolveScrollUnits` would change this value. Lets callers keep a
 * plain string (and GSAP's own `%`/keyword resolution) in the common case where
 * no rewrite is needed.
 */
export function hasResolvableScrollUnit(value: string): boolean {
  return HAS_RESOLVED_UNIT.test(value);
}

/**
 * Rewrite every `vh`/`vw`/`rem` quantity in a position string to pixels,
 * leaving keywords, `%`, `px`, bare numbers and the `+=`/`-=` operators intact.
 *
 * A leading minus is never captured, so it survives in place: `-50vh` becomes
 * `-480`, and `top-=3vh` becomes `top-=48`.
 */
export function resolveScrollUnits(
  value: string,
  factors: ScrollUnitFactors
): string {
  return value.replace(
    RESOLVED_UNIT,
    (_match, quantity: string, unit: ResolvedScrollUnit) =>
      // Two decimals keeps subpixel accuracy without bloating the published
      // string with float noise.
      String(Math.round(parseFloat(quantity) * factors[unit] * 100) / 100)
  );
}
